import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Steps,
  Typography
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { SignatureForm } from "../workReports/SignDialog";
import "../workReports/workReports.css";
import { deemedAcceptance } from "./calc";
import { methodLabels, reasonLabels } from "./labels";
import { formatDay } from "../workReports/format";

const iso = value => (value ? value.format("YYYY-MM-DD") : "");

/**
 * A small form in a dialog: `fields` render inside an antd Form, `onSubmit(values)` returns a
 * promise; the dialog stays open when it rejects.
 */
export const FormDialog = ({
  open,
  title,
  okText,
  initialValues,
  onCancel,
  onSubmit,
  children,
  width
}) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(initialValues || {});
    }
  }, [open]);
  const submit = async values => {
    setBusy(true);
    try {
      await onSubmit(values);
    } catch {
      // the caller showed the error
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      rootClassName="warranty-page"
      open={open}
      width={width}
      title={title}
      okText={okText || t("Save")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onCancel}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        {typeof children === "function" ? children(form) : children}
      </Form>
    </Modal>
  );
};

/**
 * Signing on site: our representative signs first (optional), then the customer. Reuses the
 * signature form of the Regieberichte.
 */
export const SignOnSiteDialog = ({ open, acceptance, defaultEmail, onCancel, onSign }) => {
  const { t } = useTranslation("dashboard");
  const contractorSigned = Boolean(acceptance?.signatures?.contractor?.signedAt);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (open) setStep(contractorSigned ? 1 : 0);
  }, [open]);
  const customer = (acceptance?.participants || []).find(p => p.role === "customer");
  const contractor = (acceptance?.participants || []).find(p => p.role === "contractor");

  const submit = party => async values => {
    setSubmitting(true);
    try {
      await onSign({ ...values, party });
      if (party === "contractor") setStep(1);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      rootClassName="warranty-page"
      open={open}
      width={760}
      title={t("Sign the acceptance protocol")}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      maskClosable={false}
    >
      <Steps
        size="small"
        className="mb-3"
        current={step}
        items={[{ title: t("Contractor") }, { title: t("Customer") }]}
      />
      <div className="work-reports-page">
        {step === 0 ? (
          <>
            <Typography.Paragraph type="secondary">
              {t(
                "You sign for your company first. Changing the protocol afterwards removes this signature."
              )}
            </Typography.Paragraph>
            <SignatureForm
              key="contractor"
              defaultName={contractor?.name || ""}
              showCopy={false}
              submitting={submitting}
              onSubmit={submit("contractor")}
            />
            <Button type="link" onClick={() => setStep(1)}>
              {t("Skip: only the customer signs")}
            </Button>
          </>
        ) : (
          <>
            <Typography.Paragraph type="secondary">
              {t(
                "Hand the device to the customer. With the signature the customer declares the acceptance, subject to the defects and reservations listed."
              )}
            </Typography.Paragraph>
            <SignatureForm
              key="customer"
              defaultName={customer?.name || ""}
              defaultEmail={defaultEmail}
              submitting={submitting}
              onSubmit={submit("customer")}
              onBack={contractorSigned ? undefined : () => setStep(0)}
            />
          </>
        )}
      </div>
    </Modal>
  );
};

/** Start a fictitious acceptance: notice of completion, start of use, or a deadline. */
export const RequestDialog = ({ open, acceptance, onCancel, onSubmit }) => {
  const { t } = useTranslation("dashboard");
  const methods = methodLabels(t);
  const reasons = reasonLabels(t);
  const basis = acceptance?.contractBasis;
  const options = [
    "fictitious_deadline",
    ...(basis === "vob" ? ["fictitious_completion", "fictitious_use"] : [])
  ];
  return (
    <FormDialog
      open={open}
      width={640}
      title={t("Acceptance by deadline")}
      okText={t("Start")}
      initialValues={{
        method: "fictitious_deadline",
        triggerDate: dayjs(),
        deadlineDate: acceptance?.suggestedDeadline
          ? dayjs(acceptance.suggestedDeadline)
          : dayjs().add(14, "day"),
        consumerNotice: false,
        send: Boolean(acceptance?.clientInfo?.email),
        email: acceptance?.clientInfo?.email || ""
      }}
      onCancel={onCancel}
      onSubmit={values =>
        onSubmit({
          method: values.method,
          triggerDate: iso(values.triggerDate),
          deadlineDate: values.method === "fictitious_deadline" ? iso(values.deadlineDate) : "",
          consumerNotice: Boolean(values.consumerNotice),
          send: values.method !== "fictitious_use" && Boolean(values.send),
          email: values.email
        })
      }
    >
      {form => (
        <Form.Item noStyle shouldUpdate>
          {() => {
            const v = form.getFieldsValue();
            const preview = deemedAcceptance({
              method: v.method,
              contractBasis: basis,
              triggerDate: iso(v.triggerDate),
              deadlineDate: iso(v.deadlineDate),
              consumerNotice: v.consumerNotice || v.send
            });
            return (
              <>
                <Form.Item name="method" label={t("Type of acceptance")}>
                  <Radio.Group>
                    <Space direction="vertical">
                      {options.map(m => (
                        <Radio key={m} value={m}>
                          {methods[m]}
                        </Radio>
                      ))}
                    </Space>
                  </Radio.Group>
                </Form.Item>
                <Form.Item
                  name="triggerDate"
                  label={
                    v.method === "fictitious_use"
                      ? t("The client started using the work on")
                      : v.method === "fictitious_completion"
                        ? t("The notice of completion was received on")
                        : t("Request sent on")
                  }
                  rules={[{ required: true }]}
                >
                  <DatePicker className="wty-full" format="DD.MM.YYYY" allowClear={false} />
                </Form.Item>
                {v.method === "fictitious_deadline" && (
                  <Form.Item
                    name="deadlineDate"
                    label={t("Deadline for the acceptance")}
                    rules={[{ required: true }]}
                  >
                    <DatePicker className="wty-full" format="DD.MM.YYYY" allowClear={false} />
                  </Form.Item>
                )}
                {v.method !== "fictitious_use" && (
                  <>
                    <Form.Item name="send" valuePropName="checked">
                      <Checkbox>{t("Send the letter to the customer by email now")}</Checkbox>
                    </Form.Item>
                    {v.send && (
                      <Form.Item
                        name="email"
                        label={t("Email address")}
                        rules={[{ required: true, type: "email" }]}
                      >
                        <Input type="email" />
                      </Form.Item>
                    )}
                  </>
                )}
                {basis === "bgb_consumer" && v.method === "fictitious_deadline" && !v.send && (
                  <Form.Item name="consumerNotice" valuePropName="checked">
                    <Checkbox>
                      {t(
                        "The customer was told the consequences of silence in text form (the letter of this app does that)."
                      )}
                    </Checkbox>
                  </Form.Item>
                )}
                {preview.date ? (
                  <Alert
                    type="info"
                    showIcon
                    message={t(
                      "Deemed accepted with the end of {{date}} unless the customer refuses naming a defect.",
                      {
                        date: formatDay(preview.date)
                      }
                    )}
                    description={t(
                      "Public holidays of your federal state are taken into account when saving."
                    )}
                  />
                ) : (
                  preview.reason && (
                    <Alert type="warning" showIcon message={reasons[preview.reason]} />
                  )
                )}
                <Alert
                  className="mt-3"
                  type="warning"
                  showIcon
                  message={t(
                    "The letter wording is a draft. Have it checked by your lawyer before you use it for the first time."
                  )}
                />
              </>
            );
          }}
        </Form.Item>
      )}
    </FormDialog>
  );
};

/** Acceptance declared on paper or by conduct. */
export const RecordDialog = ({ open, onCancel, onSubmit }) => {
  const { t } = useTranslation("dashboard");
  return (
    <FormDialog
      open={open}
      title={t("Record acceptance")}
      initialValues={{ method: "formal", acceptedOn: dayjs(), note: "" }}
      onCancel={onCancel}
      onSubmit={values => onSubmit({ ...values, acceptedOn: iso(values.acceptedOn) })}
    >
      <Form.Item name="method" label={t("How was it accepted?")}>
        <Select
          options={[
            { value: "formal", label: t("Protocol signed on paper") },
            { value: "implied", label: t("By conduct (e.g. moved in and paid without complaint)") }
          ]}
        />
      </Form.Item>
      <Form.Item name="acceptedOn" label={t("Accepted on")} rules={[{ required: true }]}>
        <DatePicker
          className="wty-full"
          format="DD.MM.YYYY"
          allowClear={false}
          disabledDate={d => d && d.isAfter(dayjs(), "day")}
        />
      </Form.Item>
      <Form.Item name="note" label={t("Evidence")} rules={[{ required: true, min: 3 }]}>
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={2000} />
      </Form.Item>
    </FormDialog>
  );
};

/** The customer refused the acceptance. */
export const RefuseDialog = ({ open, acceptance, onCancel, onSubmit }) => {
  const { t } = useTranslation("dashboard");
  const customer = (acceptance?.participants || []).find(p => p.role === "customer");
  return (
    <FormDialog
      open={open}
      title={t("Acceptance refused")}
      initialValues={{ on: dayjs(), name: customer?.name || "", reason: "", method: "ONSITE" }}
      onCancel={onCancel}
      onSubmit={values => onSubmit({ ...values, on: iso(values.on) })}
    >
      <Form.Item name="method" label={t("Refused")}>
        <Radio.Group
          options={[
            { value: "ONSITE", label: t("on site") },
            { value: "MANUAL", label: t("by letter or email") }
          ]}
        />
      </Form.Item>
      <Form.Item name="on" label={t("Date")} rules={[{ required: true }]}>
        <DatePicker className="wty-full" format="DD.MM.YYYY" allowClear={false} />
      </Form.Item>
      <Form.Item name="name" label={t("By")}>
        <Input maxLength={120} />
      </Form.Item>
      <Form.Item
        name="reason"
        label={t("Reason (defects named by the customer)")}
        rules={[{ required: true, min: 3 }]}
      >
        <Input.TextArea autoSize={{ minRows: 3, maxRows: 6 }} maxLength={2000} />
      </Form.Item>
    </FormDialog>
  );
};

/** Progress of one defect after the protocol: fixed by us, confirmed by the customer. */
export const DefectStatusDialog = ({ open, defect, status, onCancel, onSubmit }) => {
  const { t } = useTranslation("dashboard");
  return (
    <FormDialog
      open={open}
      title={status === "accepted" ? t("Fix confirmed by the customer") : t("Defect fixed")}
      initialValues={{
        fixedOn: defect?.fixedOn ? dayjs(defect.fixedOn) : dayjs(),
        fixNote: defect?.fixNote || "",
        confirmedOn: dayjs(),
        confirmedByName: "",
        confirmationNote: ""
      }}
      onCancel={onCancel}
      onSubmit={values =>
        onSubmit({
          status,
          fixedOn: iso(values.fixedOn),
          fixNote: values.fixNote,
          confirmedOn: iso(values.confirmedOn),
          confirmedByName: values.confirmedByName,
          confirmationNote: values.confirmationNote
        })
      }
    >
      <Typography.Paragraph strong>{defect?.description}</Typography.Paragraph>
      <Form.Item name="fixedOn" label={t("Fixed on")}>
        <DatePicker className="wty-full" format="DD.MM.YYYY" allowClear={false} />
      </Form.Item>
      <Form.Item name="fixNote" label={t("What was done")}>
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={2000} />
      </Form.Item>
      {status === "accepted" && (
        <>
          <Form.Item
            name="confirmedByName"
            label={t("Confirmed by")}
            rules={[{ required: true, min: 2 }]}
          >
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item name="confirmedOn" label={t("Confirmed on")}>
            <DatePicker className="wty-full" format="DD.MM.YYYY" allowClear={false} />
          </Form.Item>
          <Form.Item name="confirmationNote" label={t("Note")}>
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={2000} />
          </Form.Item>
        </>
      )}
    </FormDialog>
  );
};

export const SimpleTextDialog = ({
  open,
  title,
  label,
  okText,
  initial = "",
  type,
  onCancel,
  onSubmit
}) => (
  <FormDialog
    open={open}
    title={title}
    okText={okText}
    initialValues={{ value: initial }}
    onCancel={onCancel}
    onSubmit={values => onSubmit(values.value)}
  >
    <Form.Item
      name="value"
      label={label}
      rules={[{ required: true, ...(type === "email" ? { type: "email" } : { min: 3 }) }]}
    >
      {type === "email" ? (
        <Input type="email" />
      ) : (
        <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={1000} />
      )}
    </Form.Item>
  </FormDialog>
);

export const LinkResult = ({ url }) => {
  const { t } = useTranslation("dashboard");
  return (
    <Space direction="vertical" className="wty-full">
      <Typography.Text>{t("Send this link to the customer:")}</Typography.Text>
      <Typography.Paragraph copyable={{ text: url }} className="wty-link-text">
        {url}
      </Typography.Paragraph>
      {navigator.share && (
        <Button onClick={() => navigator.share({ url }).catch(() => {})}>{t("Share")}</Button>
      )}
    </Space>
  );
};
